import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import api from '../../services/api';

dayjs.locale('fr');

const WhatsAppMessages = () => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1, // Backend attend page à partir de 1
        limit: pageSize
      };
      if (statusFilter) params.status = statusFilter;
      
      const response = await api.get('/whatsapp/messages', { params });
      setMessages(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (err) {
      console.error('Erreur chargement messages:', err);
      setError('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      
      const response = await api.get('/whatsapp/stats', { params });
      // Le backend renvoie { success: true, data: { total, sent, ... } }
      setStats(response.data.data || response.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [fetchMessages, fetchStats]);

  const handleRetry = async (messageId) => {
    try {
      await api.post(`/whatsapp/retry/${messageId}`);
      setSuccess('Message renvoyé');
      fetchMessages();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du renvoi');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
      case 'delivered':
      case 'read':
        return <SuccessIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'pending':
      default:
        return <PendingIcon color="warning" fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'info';
      case 'delivered': return 'success';
      case 'read': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'sent': return 'Envoyé';
      case 'delivered': return 'Délivré';
      case 'read': return 'Lu';
      case 'failed': return 'Échoué';
      case 'pending': return 'En attente';
      default: return status;
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70
    },
    {
      field: 'subscriber',
      headerName: 'Abonné',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.subscriber?.first_name} {params.row.subscriber?.last_name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.subscriber?.phone_number}
          </Typography>
        </Box>
      )
    },
    {
      field: 'ephemeris',
      headerName: 'Date éphéméride',
      width: 130,
      renderCell: (params) => (
        params.row.ephemeris?.date 
          ? dayjs(params.row.ephemeris.date).format('DD/MM/YYYY')
          : '-'
      )
    },
    {
      field: 'message_type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'with_frame' ? 'Cadre' : 'Simple'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'sent_at',
      headerName: 'Envoyé le',
      width: 150,
      renderCell: (params) => (
        params.value 
          ? dayjs(params.value).format('DD/MM/YY HH:mm')
          : '-'
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      renderCell: (params) => (
        params.row.status === 'failed' && (
          <Tooltip title="Réessayer">
            <IconButton
              size="small"
              onClick={() => handleRetry(params.row.id)}
            >
              <RetryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      )
    }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Messages WhatsApp
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Statistiques */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.sent}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Envoyés
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.delivered}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Délivrés
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {stats.failed}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Échoués
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {stats.successRate}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Taux de succès
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filtres */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Statut"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="sent">Envoyé</MenuItem>
                <MenuItem value="delivered">Délivré</MenuItem>
                <MenuItem value="read">Lu</MenuItem>
                <MenuItem value="failed">Échoué</MenuItem>
              </Select>
            </FormControl>
            
            <DatePicker
              label="Date début"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            
            <DatePicker
              label="Date fin"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            
            <Box sx={{ flexGrow: 1 }} />
            
            <Button
              variant="outlined"
              onClick={() => {
                setStatusFilter('');
                setStartDate(null);
                setEndDate(null);
              }}
            >
              Réinitialiser
            </Button>
            
            <IconButton onClick={() => { fetchMessages(); fetchStats(); }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Tableau des messages */}
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={messages}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            getRowHeight={() => 60}
            localeText={{
              noRowsLabel: 'Aucun message',
              MuiTablePagination: {
                labelRowsPerPage: 'Par page'
              }
            }}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default WhatsAppMessages;
